import { log } from '../services/production-logger';

/**
 * Auth Helper Utilities
 *
 * Production-ready authentication helper functions that avoid circular dependencies
 * by accessing Firebase auth directly without importing from auth-service.
 *
 * Security considerations:
 * - All functions include proper error handling
 * - Sensitive data is never logged in production
 * - Auth state is validated before operations
 */

import { User } from 'firebase/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { auth } from '../firebase/firebase';

// Global __DEV__ declaration for TypeScript
declare const __DEV__: boolean;

// Constants for AsyncStorage keys
const AUTH_STORAGE_KEYS = {
  USER_DATA: '@auth:user_data',
  AUTH_TOKEN: '@auth:token',
  LAST_AUTH_CHECK: '@auth:last_check',
  SESSION_DATA: '@auth:session',
} as const;

// Cache for performance optimization
let cachedUser: User | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5000; // 5 seconds

/**
 * Get the currently authenticated user with caching for performance
 * @returns Current Firebase user or null if not authenticated
 */
export const getCurrentUser = (): User | null => {
  try {
    // Use cached value if still valid
    if (cachedUser && Date.now() - cacheTimestamp < CACHE_DURATION) {
      return cachedUser;
    }

    // Get fresh user data
    const user = auth.currentUser;

    // Update cache
    cachedUser = user;
    cacheTimestamp = Date.now();

    return user;
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error getting current user:', error);
    }
    return null;
  }
};

/**
 * Check if user is authenticated with proper validation
 * @returns true if user is authenticated and valid, false otherwise
 */
export const isAuthenticated = (): boolean => {
  try {
    const user = getCurrentUser();
    return user !== null && user.uid !== undefined && user.uid !== '';
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error checking authentication:', error);
    }
    return false;
  }
};

/**
 * Get current user ID with validation
 * @returns Current user's UID or null if not authenticated
 */
export const getCurrentUserId = (): string | null => {
  try {
    const user = getCurrentUser();
    return user?.uid || null;
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error getting user ID:', error);
    }
    return null;
  }
};

/**
 * Get current user email with validation
 * @returns Current user's email or null if not authenticated
 */
export const getCurrentUserEmail = (): string | null => {
  try {
    const user = getCurrentUser();
    return user?.email || null;
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error getting user email:', error);
    }
    return null;
  }
};

/**
 * Clear auth-related data from AsyncStorage and cache
 * This ensures clean logout and prevents stale auth data
 */
export const clearAuthData = async (): Promise<void> => {
  try {
    // Clear cache
    cachedUser = null;
    cacheTimestamp = 0;

    // Clear AsyncStorage data based on platform
    if (Platform.OS !== 'web') {
      const keysToRemove = Object.values(AUTH_STORAGE_KEYS);

      // Add legacy keys that might exist
      keysToRemove.push('authUser', '@userProfile', '@auth_token');

      await AsyncStorage.multiRemove(keysToRemove);

      if (__DEV__) {
        log.debug('[Auth Helpers] Auth data cleared from AsyncStorage');
      }
    } else if (typeof window !== 'undefined' && window.localStorage) {
      // For web, clear localStorage
      Object.values(AUTH_STORAGE_KEYS).forEach(key => {
        window.localStorage.removeItem(key);
      });

      // Clear legacy keys
      window.localStorage.removeItem('authUser');
      window.localStorage.removeItem('@userProfile');
    }
  } catch (error) {
    // Don't throw in production, just log
    if (__DEV__) {
      log.error('[Auth Helpers] Error clearing auth data:', error);
    }
  }
};

/**
 * Decode URL-safe Base64 string (used by Firebase JWT tokens)
 * @param str URL-safe Base64 string
 * @returns Decoded string
 */
const decodeBase64Url = (str: string): string => {
  try {
    // Replace URL-safe characters with standard Base64 characters
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if necessary
    const pad = base64.length % 4;
    if (pad) {
      if (pad === 1) {
        throw new Error('Invalid base64 string');
      }
      base64 += new Array(5 - pad).join('=');
    }
    
    // Decode using standard atob
    return atob(base64);
  } catch (error) {
    if (__DEV__) {
      log.debug('[Auth Helpers] Base64 decode error:', error);
    }
    throw new Error('Failed to decode base64');
  }
};

/**
 * Validate user session is still active
 * @returns true if session is valid, false otherwise
 */
export const isSessionValid = async (): Promise<boolean> => {
  try {
    const user = getCurrentUser();
    if (!user) return false;

    // Check if token is still valid
    const token = await user.getIdToken(false);
    if (!token) return false;

    // Parse JWT token to check expiration
    const parts = token.split('.');
    if (parts.length !== 3) {
      if (__DEV__) {
        log.debug('[Auth Helpers] Invalid JWT format');
      }
      return false;
    }

    try {
      // Decode the payload (second part of JWT)
      const decodedPayload = decodeBase64Url(parts[1]);
      const payload = JSON.parse(decodedPayload);
      
      // Check expiration
      const expirationTime = payload.exp * 1000;
      return Date.now() < expirationTime;
    } catch (decodeError) {
      if (__DEV__) {
        log.debug('[Auth Helpers] Error decoding JWT payload:', decodeError);
      }
      // If we can't decode the token, assume it's invalid
      return false;
    }
  } catch (error) {
    if (__DEV__) {
      log.debug('[Auth Helpers] Error validating session:', error);
    }
    return false;
  }
};

/**
 * Force refresh of authentication token
 * @returns New token or null if refresh fails
 */
export const refreshAuthToken = async (): Promise<string | null> => {
  try {
    const user = getCurrentUser();
    if (!user) return null;

    // Force token refresh
    const token = await user.getIdToken(true);

    // Store refreshed token
    if (Platform.OS !== 'web') {
      await AsyncStorage.setItem(AUTH_STORAGE_KEYS.AUTH_TOKEN, token);
    }

    return token;
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error refreshing token:', error);
    }
    return null;
  }
};

/**
 * Get user's authentication token with caching
 * @param forceRefresh - Force token refresh even if cached
 * @returns Authentication token or null
 */
export const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
  try {
    const user = getCurrentUser();
    if (!user) return null;

    return await user.getIdToken(forceRefresh);
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error getting auth token:', error);
    }
    return null;
  }
};

/**
 * Check if user email is verified
 * @returns true if email is verified, false otherwise
 */
export const isEmailVerified = (): boolean => {
  try {
    const user = getCurrentUser();
    return user?.emailVerified || false;
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error checking email verification:', error);
    }
    return false;
  }
};

/**
 * Get user's display name with fallback
 * @returns Display name or email prefix as fallback
 */
export const getUserDisplayName = (): string => {
  try {
    const user = getCurrentUser();
    if (!user) return 'Guest';

    // Return display name if available
    if (user.displayName) return user.displayName;

    // Fallback to email prefix
    if (user.email) {
      return user.email.split('@')[0];
    }

    return 'User';
  } catch (error) {
    if (__DEV__) {
      log.error('[Auth Helpers] Error getting display name:', error);
    }
    return 'User';
  }
};

